import { inject } from 'mobx-react';
import * as debounce from 'lodash.debounce';
import * as React from 'react';

import { AnalyticsStore } from 'bernie-core';
import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';

import { UitkSlider } from 'uitk-react-slider';

import { FormFieldBinding } from './form-field-binding';
import { SearchFormFieldProps } from 'components/common/search-form';

export type LocalizedSliderProps = LocalizedComponentProps & SearchFormFieldProps & SliderProps;

export interface SliderProps {
  analytics?: AnalyticsStore;
}

export interface SliderState {
  lowerValue: number;
  upperValue: number;
}

@withLocalization
@FormFieldBinding
@inject('analytics')
export class Slider extends React.Component<LocalizedSliderProps, SliderState> {
  private sendTrackingDebounced;

  constructor(props) {
    super(props);

    this.handleStateChange = this.handleStateChange.bind(this);
    this.handleAfterChange = this.handleAfterChange.bind(this);
    this.formatValueForDisplay = this.formatValueForDisplay.bind(this);
    this.formatSecondaryValueForDisplay = this.formatSecondaryValueForDisplay.bind(this);
    this.sendTrackingDebounced = debounce(this.sendTracking, 300);

    const [lowerValue, upperValue] = this.sliderRange;
    this.state = {
      lowerValue,
      upperValue
    };
  }

  public componentDidMount() {
    /*
    Used to support legacy 'price' bucket values (i.e. &price=1&price=2) that get translated by the backend to fit with the slider format.
     */
    const { model } = this.props;
    const key = 'priceFilterBuckets';
    if (model && model[key] && model[key].length > 0) {
      const [lowerValue, upperValue] = model[key];
      this.handleStateChange(lowerValue, upperValue);
      this.handleAfterChange(lowerValue, upperValue, false);
    }
  }

  public componentDidUpdate(prevProps: LocalizedSliderProps): void {
    const { model: prevModel } = prevProps;
    const { model } = this.props;
    if (model !== prevModel) {
      const [lowerValue, upperValue] = this.sliderRange;
      this.handleStateChange(lowerValue, upperValue);
    }
  }

  private get options() {
    const { sortAndFilterOptions, modelKey } = this.props;
    const options = sortAndFilterOptions && sortAndFilterOptions[modelKey];

    return options || {};
  }

  private get sliderRange(): number[] {
    const { model, modelKey, fieldNameOverrides } = this.props;
    const { min: defaultMin, max: defaultMax } = this.options;
    const key = (fieldNameOverrides && fieldNameOverrides[modelKey]) || modelKey;
    const selectedRange = model && model[key] && model[key].length > 0 ? model[key] : [defaultMin, defaultMax];

    return selectedRange.map(parseFloat);
  }

  private handleStateChange(lowerValue: number, upperValue: number): void {
    this.setState({ lowerValue, upperValue });
  }

  private handleAfterChange(lowerValue: number, upperValue: number, track: boolean = true): void {
    const { updateModel } = this.props;
    const { name } = this.options;
    const sliderValue = [lowerValue, upperValue];

    if (track) {
      this.sendTrackingDebounced(sliderValue);
    }

    if (typeof updateModel === 'function') {
      updateModel({
        target: {
          name,
          value: sliderValue
        }
      });
    }
  }

  private sendTracking(value: number[]) {
    const { analytics } = this.props;
    const { name } = this.options;
    const suffix = `${name}Slider.${value}`;
    analytics.clickTracker(`.${suffix}`, suffix, {}, () => null)();
  }

  private formatValueForDisplay(value: number): string {
    const subLabel = this.options.subLabel || '';

    return `${subLabel}${value}`;
  }

  private formatSecondaryValueForDisplay(value: number): string {
    const subLabel = this.options.subLabel || '';
    const displayValue = value === 0 ? `${subLabel}${this.options.max}+` : `${subLabel}${value}`;

    return value === this.options.max ? `${displayValue}+` : displayValue;
  }

  public render() {
    const { l10n } = this.props;
    const { formatText } = l10n;
    const { label, name, max, min, step } = this.options;
    const { lowerValue, upperValue } = this.state;

    return (
      <fieldset className="all-b-margin-six" data-stid="price-slider-container">
        <legend className="filter-title uitk-type-heading-400 uitk-type-bold">{label}</legend>
        <UitkSlider
          type="dual"
          min={min}
          max={max}
          step={step}
          name={name}
          lowerValue={lowerValue}
          upperValue={upperValue}
          id={`${this.props.modelKey}-slider`}
          handleChange={this.handleStateChange}
          handleAfterChange={this.handleAfterChange}
          formatValueForDisplay={this.formatValueForDisplay}
          primaryLiveAnnounceMsg={formatText('hotels.searchResults.filtersDialog.price.minimumLiveAnnounce')}
          secondaryLiveAnnounceMsg={formatText('hotels.searchResults.filtersDialog.price.maximumLiveAnnounce')}
          formatSecondaryValueForDisplay={this.formatSecondaryValueForDisplay}
        />
      </fieldset>
    );
  }
}
